import { Component, Input, OnChanges, OnInit, ViewContainerRef } from '@angular/core';
import { MdDialog, MdDialogConfig, MdDialogRef } from '@angular/material';

import { Observable } from 'rxjs/Observable';

import { EntityIdDetails } from 'app/shared/models/entity-id-details.model';
import { EntityMonitoringService } from 'app/entities/monitoring/entity-monitoring.service';
import { SnackBarService } from 'app/services/snackbar.service';
import { AuthenticationGuard } from 'app/services/authentication-guard.service';

import { SanctionService } from 'app/entities/monitoring/actual/sanction/sanction.service';
import { PoliticallyExposedService } from 'app/entities/monitoring/actual/politicallyexposed/politically-exposed.service';
import { BadPressService } from 'app/entities/monitoring/actual/badpress/bad-press.service';

import { WarmUpService, ApplicationTypeConstants } from 'app/services/warmup/warmup.service';
import { ApplicationValue } from 'app/services/warmup/application-type.model';

import { ResolveBulkHitsComponent } from 'app/entities/monitoring/dashboard/resolve-bulk-hits/resolve-bulk-hits.component';
import { HitDetailDialogComponent } from 'app/entities/monitoring/dashboard/hit-detail-dialog/hit-detail-dialog.component';
import { VerifyNowDialogComponent } from 'app/entities/monitoring/dashboard/verify-now-dialog/verify-now-dialog.component';
import { formatDisplayDateTime } from 'app/shared/utils/date-format.util';

type MonitoringHitType = 'Sanction' | 'Pep' | 'Badpress';
type MonitoringListType = 'open' | 'confirmed' | 'history';

interface MonitoringRow {
  id: string;
  type: MonitoringHitType;
  listType: MonitoringListType;
  hitDate?: any;
  source?: string;
  /** Full name from the list entity (display column "List name"). */
  listName: string;
  /** Match tokens only (display column "Hit"). */
  hitTokens: string;
  /** Alias of listName — kept for bulk-resolve dialog title. */
  title: string;
  subTitle?: string;
  matchResolution?: any;
  falseMatchReason?: any;
  comments?: string;
  relationEntityId?: number;
  raw: any;
}

type MonitoringStatus = 'open' | 'positive' | 'negative' | 'inconclusive';

@Component({
  selector: 'monitoring-dashboard',
  templateUrl: './monitoring-dashboard.component.html',
  styleUrls: ['./monitoring-dashboard.component.css'],
  providers: [SanctionService, BadPressService]
})
export class MonitoringDashboardComponent implements OnInit, OnChanges {
  @Input() entityIdDetails: EntityIdDetails;

  showAllHistorical = false;

  openRows: MonitoringRow[] = [];
  historyRows: MonitoringRow[] = [];
  confirmedRows: MonitoringRow[] = [];

  /**
   * Rows that were resolved in this UI session.
   * We keep them visible immediately (especially negatives) even if "Show all historical" is off.
   */
  recentResolvedRows: MonitoringRow[] = [];

  rows: MonitoringRow[] = [];
  filteredRows: MonitoringRow[] = [];
  filterText = '';
  selectedRowKeys = new Set<string>();

  openCounts = {
    total: 0,
    sanction: 0,
    pep: 0,
    badpress: 0
  };

  matchCounts = {
    sanction: 0,
    pep: 0,
    badpress: 0
  };

  loadingOpen = false;
  loadingHistory = false;
  verifyingNow = false;
  trackingNow = false;
  deletingAllHits = false;
  rescreening = false;
  isMonitored = false;
  monitoringStatus: any = null;

  dialogRef: MdDialogRef<any>;

  sanctionPepResolutions: ApplicationValue[];
  badPressResolutions: ApplicationValue[];
  falseMatchReasons: ApplicationValue[];

  constructor(
    public dialog: MdDialog,
    public viewContainerRef: ViewContainerRef,
    private entityMonitoringService: EntityMonitoringService,
    private sanctionService: SanctionService,
    private politicallyExposedService: PoliticallyExposedService,
    private badPressService: BadPressService,
    private warmUpService: WarmUpService,
    private snackBarService: SnackBarService,
    private authenticationGuard: AuthenticationGuard
  ) { }

  isAdminUser(): boolean {
    return this.authenticationGuard && this.authenticationGuard.isAdminUser();
  }

  ngOnInit() {
    this.setUpSanctionPepResolutions(
      this.warmUpService.getApplicationValue(ApplicationTypeConstants.SANCTION_MATCH_RESOLUTION));
    this.setUpBadPressResolutions(
      this.warmUpService.getApplicationValue(ApplicationTypeConstants.PANAMA_PAPERS_MATCH_RESOLUTION));
    this.setUpFalseMatchReasons(
      this.warmUpService.getApplicationValue(ApplicationTypeConstants.FALSE_MATCH_REASONS));

    this.politicallyExposedService.addPEPMatch.subscribe(event => {
      if (event === 'AddedPepMatch') {
        this.refreshAll();
      }
    });

    this.refreshAll();
  }

  ngOnChanges() {
    this.refreshAll();
  }

  refreshAll() {
    if (!this.entityIdDetails || !this.entityIdDetails.entityId) {
      return;
    }
    this.clearSelection();
    this.loadMonitoringStatus();
    this.loadOpenHits();
    this.loadHistory();
  }

  loadMonitoringStatus() {
    if (!this.entityIdDetails || !this.entityIdDetails.entityId) {
      return;
    }
    this.entityMonitoringService.getMonitoringStatus(this.entityIdDetails.entityId).subscribe(
      response => {
        const arrResult = response && response.arrResult;
        this.monitoringStatus = arrResult || null;
        this.isMonitored = !!(arrResult && arrResult.isMonitored);
      },
      _err => {
        this.monitoringStatus = null;
        this.isMonitored = false;
      }
    );
  }

  setUpSanctionPepResolutions(dropDownValues: any) {
    if (dropDownValues instanceof Observable) {
      dropDownValues.subscribe(response => {
        this.warmUpService.setApplicationTypeInLS(response);
        this.sanctionPepResolutions = response.values;
        this.recomputeConfirmedRows();
      });
    } else {
      this.sanctionPepResolutions = dropDownValues;
      this.recomputeConfirmedRows();
    }
  }

  setUpBadPressResolutions(dropDownValues: any) {
    if (dropDownValues instanceof Observable) {
      dropDownValues.subscribe(response => {
        this.warmUpService.setApplicationTypeInLS(response);
        this.badPressResolutions = response.values;
        this.recomputeConfirmedRows();
      });
    } else {
      this.badPressResolutions = dropDownValues;
      this.recomputeConfirmedRows();
    }
  }

  setUpFalseMatchReasons(dropDownValues: any) {
    if (dropDownValues instanceof Observable) {
      dropDownValues.subscribe(response => {
        this.warmUpService.setApplicationTypeInLS(response);
        this.falseMatchReasons = response.values;
      });
    } else {
      this.falseMatchReasons = dropDownValues;
    }
  }

  isPerson(): boolean {
    return this.entityIdDetails && this.entityIdDetails.isPerson;
  }

  // ---- Loading / transforming ------------------------------------------------

  /** Default when a monitoring API fails (e.g. PEP timeout with many hits). Ensures other hits still display. */
  private readonly emptySuggestion = { suggestionList: [] };

  loadOpenHits() {
    const entityId = this.entityIdDetails.entityId;

    this.loadingOpen = true;
    this.openRows = [];

    const emptyOnError = (obs: Observable<any>) =>
      obs.catch(() => Observable.of(this.emptySuggestion));

    const requests: Array<Observable<any>> = [];

    requests.push(emptyOnError(this.sanctionService.getSanctionInfoMatchDesc(entityId, this.entityIdDetails.entityType)));
    requests.push(emptyOnError(this.badPressService.getBadPressInfoMatches(entityId, this.entityIdDetails.entityType)));
    if (this.isPerson()) {
      requests.push(emptyOnError(this.politicallyExposedService.getPoliticallyExposedInfoMatchDesc(entityId, this.entityIdDetails.entityType)));
    }

    Observable.forkJoin(requests).subscribe(responses => {
      const sanctionResp = responses[0];
      const badPressResp = responses[1];
      const pepResp = this.isPerson() ? responses[2] : null;

      const sanctionList = (sanctionResp && sanctionResp.suggestionList) || [];
      const badPressList = (badPressResp && badPressResp.suggestionList) || [];
      const pepList = (pepResp && pepResp.suggestionList) || [];

      const sanctionRows = sanctionList.map((r, i) => this.toOpenRow('Sanction', r, i));
      const badPressRows = badPressList.map((r, i) => this.toOpenRow('Badpress', r, i));
      const pepRows = pepList.map((r, i) => this.toOpenRow('Pep', r, i));

      this.openRows = ([] as MonitoringRow[]).concat(sanctionRows, pepRows, badPressRows);
      this.updateOpenCounts();
      this.rebuildRows();
      this.loadingOpen = false;
    }, _err => {
      this.loadingOpen = false;
    });
  }

  loadHistory() {
    const entityId = this.entityIdDetails.entityId;
    this.loadingHistory = true;

    const emptyOnError = (obs: Observable<any>) =>
      obs.catch(() => Observable.of(this.emptySuggestion));

    const requests: Array<Observable<any>> = [
      emptyOnError(this.entityMonitoringService.getSanctionHistory(entityId)),
      emptyOnError(this.entityMonitoringService.getBadPressHistory(entityId)),
    ];
    if (this.isPerson()) {
      requests.push(emptyOnError(this.entityMonitoringService.getPepHistory(entityId)));
    }

    Observable.forkJoin(requests).subscribe(responses => {
      const sanctionResp = responses[0];
      const badPressResp = responses[1];
      const pepResp = this.isPerson() ? responses[2] : null;

      const sanctionList = (sanctionResp && sanctionResp.suggestionList) || [];
      const badPressList = (badPressResp && badPressResp.suggestionList) || [];
      const pepList = (pepResp && pepResp.suggestionList) || [];

      const sanctionRows = sanctionList.map((r, i) => this.toHistoryRow('Sanction', r, i));
      const badPressRows = badPressList.map((r, i) => this.toHistoryRow('Badpress', r, i));
      const pepRows = pepList.map((r, i) => this.toHistoryRow('Pep', r, i));

      this.historyRows = ([] as MonitoringRow[]).concat(sanctionRows, pepRows, badPressRows);
      this.recomputeConfirmedRows();

      this.updateMatchCounts();
      this.rebuildRows();
      this.loadingHistory = false;
    }, _err => {
      this.loadingHistory = false;
    });
  }

  private recomputeConfirmedRows() {
    if (!this.historyRows) {
      this.historyRows = [];
    }
    this.confirmedRows = this.historyRows.filter(r => this.isPositiveResolution(r.type, r.raw && r.raw.matchResolution));
    this.updateMatchCounts();
  }

  private toOpenRow(type: MonitoringHitType, record: any, idx: number): MonitoringRow {
    const listName = this.getListName(type, record);
    const hitTokens = this.getHitTokens(type, record);
    return {
      id: `open:${type}:${idx}`,
      type,
      listType: 'open',
      hitDate: record && record.date,
      source: this.getSource(type, record),
      listName,
      hitTokens,
      title: listName,
      subTitle: this.getSubTitle(type, record),
      matchResolution: record && record.matchResolution,
      falseMatchReason: record && record.falseMatchReason,
      comments: record && record.comments,
      relationEntityId: record && record.relationEntityId,
      raw: record
    };
  }

  private toHistoryRow(type: MonitoringHitType, record: any, idx: number): MonitoringRow {
    const listName = this.getListName(type, record);
    const hitTokens = this.getHitTokens(type, record);
    return {
      id: `history:${type}:${idx}`,
      type,
      listType: 'history',
      hitDate: record && record.date,
      source: this.getSource(type, record),
      listName,
      hitTokens,
      title: listName,
      subTitle: this.getSubTitle(type, record),
      matchResolution: record && record.matchResolution,
      falseMatchReason: record && record.falseMatchReason,
      comments: record && record.comments,
      relationEntityId: record && record.relationEntityId,
      raw: record
    };
  }

  /** Full name from the list entity (not match tokens). */
  private getListName(type: MonitoringHitType, record: any): string {
    if (!record) {
      return '';
    }
    if (type === 'Sanction' && record.sanctionEntity) {
      const ent = record.sanctionEntity;
      const fromJson = this.extractListDisplayName(ent.jsonData);
      if (fromJson) {
        return fromJson;
      }
      // Backend resolves display name into globalName; skip when it is clearly just the tokens.
      const tokenHint = (ent.matchedTokens != null ? String(ent.matchedTokens).trim() : '')
        || this.extractMatchedTokens(ent.jsonData);
      const global = (ent.globalName || '').trim();
      if (global && (!tokenHint || global.toLowerCase() !== tokenHint.toLowerCase())) {
        return global;
      }
      return `${ent.firstName || ''} ${ent.lastName || ''}`.trim();
    }
    if (type === 'Pep' && record.pepEntity) {
      return this.extractListDisplayName(record.payloadJson)
        || (record.pepEntity.globalName || '').trim()
        || this.extractListDisplayName(record.pepEntity)
        || `${record.pepEntity.firstName || ''} ${record.pepEntity.lastName || ''}`.trim();
    }
    if (type === 'Badpress' && record.badPressEntity) {
      const bp = record.badPressEntity;
      if (bp.title && String(bp.title).trim()) {
        return String(bp.title).trim();
      }
      const fromJson = this.extractAdverseHeadline(bp.jsonData)
        || this.extractListDisplayName(bp.jsonData);
      return fromJson || bp.name || '';
    }
    return '';
  }

  /** Article headline from Beamar negative-news JSON when VO.title is absent. */
  private extractAdverseHeadline(jsonRaw: any): string {
    if (!jsonRaw) {
      return '';
    }
    try {
      const obj = typeof jsonRaw === 'string' ? JSON.parse(jsonRaw) : jsonRaw;
      if (!obj || typeof obj !== 'object') {
        return '';
      }
      const title = (obj.record && obj.record.title) || obj.title;
      return title && String(title).trim() ? String(title).trim() : '';
    } catch (_e) {
      return '';
    }
  }

  /** User-facing type label (Adverse media / Leak papers vs raw Badpress). */
  typeLabel(row: MonitoringRow): string {
    if (!row) {
      return '';
    }
    if (row.type === 'Pep') {
      return 'PEP';
    }
    if (row.type === 'Badpress') {
      if (this.isAdverseMediaRow(row)) {
        return 'Adverse media';
      }
      if (this.isLeakPapersRow(row)) {
        return 'Leak papers';
      }
      return 'Bad press';
    }
    return row.type;
  }

  typePillClass(row: MonitoringRow): string {
    if (row && row.type === 'Badpress') {
      if (this.isAdverseMediaRow(row)) {
        return 'type-pill type-adversemedia';
      }
      if (this.isLeakPapersRow(row)) {
        return 'type-pill type-leakpapers';
      }
    }
    return row ? `type-pill type-${row.type.toLowerCase()}` : 'type-pill';
  }

  private isAdverseMediaRow(row: MonitoringRow): boolean {
    const bp = row && row.raw && row.raw.badPressEntity;
    if (!bp) {
      return false;
    }
    const src = bp.source ? String(bp.source).toUpperCase() : '';
    if (src === 'NEWS') {
      return true;
    }
    return !!(bp.title || bp.url || bp.provider_name);
  }

  private isLeakPapersRow(row: MonitoringRow): boolean {
    const bp = row && row.raw && row.raw.badPressEntity;
    const src = bp && bp.source ? String(bp.source).toUpperCase() : '';
    return !!src && src.indexOf('LEAK') >= 0;
  }

  /** Tokens that matched the screen (Beamar matchedNames / matchedTokens). */
  private getHitTokens(type: MonitoringHitType, record: any): string {
    if (!record) {
      return '';
    }
    if (type === 'Sanction' && record.sanctionEntity) {
      const ent = record.sanctionEntity;
      const fromApi = ent.matchedTokens != null ? String(ent.matchedTokens).trim() : '';
      if (fromApi) {
        return fromApi;
      }
      const fromJson = this.extractMatchedTokens(ent.jsonData);
      if (fromJson) {
        return fromJson;
      }
      // Legacy: tokens were stored as globalName while JSON still has the real list name.
      const listFromJson = this.extractListDisplayName(ent.jsonData);
      const global = (ent.globalName || '').trim();
      if (global && listFromJson && global.toLowerCase() !== listFromJson.toLowerCase()) {
        return global;
      }
      return '';
    }
    if (type === 'Pep' && record.pepEntity) {
      if (record.pepEntity.matchedTokens) {
        return String(record.pepEntity.matchedTokens).trim();
      }
      return this.extractMatchedTokens(record.payloadJson);
    }
    if (type === 'Badpress' && record.badPressEntity) {
      if (record.badPressEntity.matchedTokens) {
        return String(record.badPressEntity.matchedTokens).trim();
      }
      return this.extractMatchedTokens(record.badPressEntity.jsonData);
    }
    return '';
  }

  private extractMatchedTokens(jsonRaw: any): string {
    if (!jsonRaw) {
      return '';
    }
    try {
      const obj = typeof jsonRaw === 'string' ? JSON.parse(jsonRaw) : jsonRaw;
      if (!obj || !Array.isArray(obj.matchedNames)) {
        return '';
      }
      return obj.matchedNames
        .map((x: any) => x == null ? '' : String(x).trim())
        .filter((s: string) => !!s)
        .join(' / ');
    } catch (_e) {
      return '';
    }
  }

  /**
   * Pull the canonical list name from stored Beamar JSON when present.
   * Prefer matchedName (full list name), then record.names / names.
   * Never use matchedNames (those are Hit tokens).
   */
  private extractListDisplayName(jsonRaw: any): string {
    if (!jsonRaw) {
      return '';
    }
    try {
      const obj = typeof jsonRaw === 'string' ? JSON.parse(jsonRaw) : jsonRaw;
      if (!obj || typeof obj !== 'object') {
        return '';
      }
      const fromObj = this.firstDisplayNameField(obj);
      if (fromObj) {
        return fromObj;
      }
      if (obj.record && typeof obj.record === 'object') {
        const fromRecord = this.firstDisplayNameField(obj.record);
        if (fromRecord) {
          return fromRecord;
        }
      }
      const recordNames = obj.record && obj.record.names;
      const names = Array.isArray(recordNames) && recordNames.length ? recordNames : obj.names;
      // Skip top-level names when it is clearly the matchedNames token list.
      if (Array.isArray(names) && names !== obj.matchedNames) {
        for (let i = 0; i < names.length; i++) {
          if (names[i] && String(names[i]).trim()) {
            return String(names[i]).trim();
          }
        }
      }
    } catch (_e) {
      return '';
    }
    return '';
  }

  private firstDisplayNameField(obj: any): string {
    if (!obj || typeof obj !== 'object') {
      return '';
    }
    const keys = ['matchedName', 'primary_name', 'globalName', 'global_name', 'name', 'fullName', 'full_name'];
    for (let i = 0; i < keys.length; i++) {
      const v = obj[keys[i]];
      if (v && String(v).trim()) {
        return String(v).trim();
      }
    }
    const first = obj.firstName || obj.first_name;
    const last = obj.lastName || obj.last_name;
    const combined = `${first || ''} ${last || ''}`.trim();
    return combined;
  }

  private getSubTitle(type: MonitoringHitType, record: any): string {
    if (!record) {
      return '';
    }
    if (type === 'Sanction' && record.sanctionEntity) {
      return record.sanctionEntity.listName || '';
    }
    if (type === 'Pep' && record.pepEntity) {
      return record.pepEntity.designation || record.pepEntity.country || '';
    }
    if (type === 'Badpress' && record.badPressEntity) {
      const bp = record.badPressEntity;
      const src = bp.source ? String(bp.source).toUpperCase() : '';
      if (src === 'NEWS' || bp.title || bp.url || bp.provider_name) {
        const parts: string[] = [];
        if (bp.provider_name) {
          parts.push(String(bp.provider_name));
        }
        const channel = this.extractAdverseSourceChannel(bp.jsonData);
        if (channel) {
          parts.push(channel);
        }
        if (parts.length) {
          return parts.join(' · ');
        }
        return 'Adverse media';
      }
      if (src.indexOf('LEAK') >= 0) {
        return 'Leak papers';
      }
      return bp.source || bp.recordType || '';
    }
    return '';
  }

  private extractAdverseSourceChannel(jsonRaw: any): string {
    if (!jsonRaw) {
      return '';
    }
    try {
      const obj = typeof jsonRaw === 'string' ? JSON.parse(jsonRaw) : jsonRaw;
      const ch = (obj && obj.record && obj.record.sourceChannel) || (obj && obj.sourceChannel);
      if (!ch) {
        return '';
      }
      const s = String(ch);
      if (s === 'gdelt') {
        return 'GDELT';
      }
      if (s === 'bing') {
        return 'Bing';
      }
      return s;
    } catch (_e) {
      return '';
    }
  }

  private getSource(type: MonitoringHitType, record: any): string {
    const ent = record && (record.sanctionEntity || record.pepEntity || record.badPressEntity);
    return (ent && ent.source) || 'N/A';
  }

  // ---- Counters / mapping ----------------------------------------------------

  updateOpenCounts() {
    const sanction = this.openRows.filter(r => r.type === 'Sanction').length;
    const pep = this.openRows.filter(r => r.type === 'Pep').length;
    const badpress = this.openRows.filter(r => r.type === 'Badpress').length;
    this.openCounts = {
      total: sanction + pep + badpress,
      sanction,
      pep,
      badpress
    };
  }

  updateMatchCounts() {
    const sanction = this.confirmedRows.filter(r => r.type === 'Sanction').length;
    const pep = this.confirmedRows.filter(r => r.type === 'Pep').length;
    const badpress = this.confirmedRows.filter(r => r.type === 'Badpress').length;
    this.matchCounts = { sanction, pep, badpress };
  }

  getResolutionName(type: MonitoringHitType, id: any): string {
    if (id === null || id === undefined) {
      return 'Probable';
    }
    const list = (type === 'Badpress') ? this.badPressResolutions : this.sanctionPepResolutions;
    if (list) {
      const match = list.find(v => v.id === id);
      if (match) {
        return match.name;
      }
    }
    return 'Probable';
  }

  getFalseMatchReasonName(id: any): string {
    if (id === null || id === undefined) {
      return 'N/A';
    }
    if (this.falseMatchReasons) {
      const match = this.falseMatchReasons.find(v => v.id === id);
      if (match) {
        return match.name;
      }
    }
    return 'N/A';
  }

  isPositiveResolution(type: MonitoringHitType, id: any): boolean {
    const name = this.getResolutionName(type, id);
    return /true/i.test(name);
  }

  // ---- Unified list + filtering ---------------------------------------------

  setFilterText(v: string) {
    this.filterText = v || '';
    this.applyFilter();
  }

  onToggleShowAllHistorical() {
    this.rebuildRows();
  }

  private rebuildRows() {
    // Default: only open hits (to resolve). Checkbox "Show all historical" reveals resolved hits.
    const nonPositiveHistory = this.historyRows.filter(r => !this.isPositiveResolution(r.type, r.raw && r.raw.matchResolution));
    const resolvedPart = this.showAllHistorical
      ? ([...this.confirmedRows, ...nonPositiveHistory] as MonitoringRow[])
      : [];

    const recent = this.dedupeByKey(this.recentResolvedRows);

    const combined = ([] as MonitoringRow[])
      .concat(this.openRows, resolvedPart, this.showAllHistorical ? recent : []);
    const all = this.dedupeByKey(combined);

    // Sort: open first, then positives, then negatives/inconclusive; newest first where possible.
    this.rows = all.sort((a, b) => {
      const rank = (r: MonitoringRow) => {
        const st = this.getStatus(r);
        if (st === 'open') { return 0; }
        if (st === 'positive') { return 1; }
        if (st === 'negative') { return 2; }
        return 3;
      };
      const ra = rank(a);
      const rb = rank(b);
      if (ra !== rb) { return ra - rb; }
      const da = new Date(a.hitDate || 0).getTime();
      const db = new Date(b.hitDate || 0).getTime();
      return db - da;
    });

    this.applyFilter();
  }

  private applyFilter() {
    this.filteredRows = this.rows.filter(r => this.matchesFilter(r, this.filterText));
  }

  private matchesFilter(r: MonitoringRow, text: string): boolean {
    if (!text) {
      return true;
    }
    const q = text.toLowerCase();
    const hay = [
      r.type,
      r.listName,
      r.hitTokens,
      r.title,
      r.subTitle,
      r.source,
      this.getResolutionName(r.type, r.matchResolution),
      this.getFalseMatchReasonName(r.falseMatchReason),
      r.comments
    ].filter(Boolean).join(' ').toLowerCase();
    return hay.indexOf(q) !== -1;
  }

  private dedupeByKey(rows: MonitoringRow[]): MonitoringRow[] {
    const seen = new Set<string>();
    const out: MonitoringRow[] = [];
    for (const r of (rows || [])) {
      const k = this.getRowKey(r);
      if (!seen.has(k)) {
        seen.add(k);
        out.push(r);
      }
    }
    return out;
  }

  private getRowKey(row: MonitoringRow): string {
    if (!row) { return ''; }
    const entity =
      (row.raw && row.raw.sanctionEntity && row.raw.sanctionEntity.id)
      || (row.raw && row.raw.pepEntity && row.raw.pepEntity.id)
      || (row.raw && row.raw.badPressEntity && row.raw.badPressEntity.id)
      || '';
    return `${row.type}:${row.relationEntityId || ''}:${entity}:${row.listName || row.title || ''}`;
  }

  getStatus(row: MonitoringRow): MonitoringStatus {
    if (!row || row.listType === 'open') {
      return 'open';
    }
    const name = this.getResolutionName(row.type, row.matchResolution);
    if (/true/i.test(name)) { return 'positive'; }
    if (/false/i.test(name)) { return 'negative'; }
    return 'inconclusive';
  }

  getStatusLabel(row: MonitoringRow): string {
    const st = this.getStatus(row);
    if (st === 'open') { return 'Open'; }
    if (st === 'positive') { return 'Positive'; }
    if (st === 'negative') { return 'Negative'; }
    return 'Inconclusive';
  }

  getStatusPillClass(row: MonitoringRow): string {
    return `status-pill status-${this.getStatus(row)}`;
  }

  // ---- Actions ---------------------------------------------------------------

  /** Last-screened timestamp surfaced from `getMonitoringStatus`, when available. */
  get lastCheckedAt(): string {
    const ms = this.monitoringStatus
      && (this.monitoringStatus.lastScreenedAt
        || this.monitoringStatus.lastCheckedAt
        || this.monitoringStatus.updatedAt);
    if (!ms) { return null; }
    try {
      return formatDisplayDateTime(ms) || String(ms);
    } catch (_e) {
      return String(ms);
    }
  }

  get hasOpenAdverseMedia(): boolean {
    return (this.openRows || []).some(r => this.isAdverseMediaRow(r));
  }

  /**
   * Compact clean-screen line for adverse media when the entity is monitored
   * and there are no open NEWS hits.
   */
  get adverseMediaCleanStatus(): string {
    if (!this.isMonitored) {
      return '';
    }
    if (!this.hasOpenAdverseMedia) {
      return 'No open adverse media hits';
    }
    const openAdverse = (this.openRows || []).filter(r => this.isAdverseMediaRow(r)).length;
    return openAdverse === 1
      ? '1 open adverse media hit'
      : `${openAdverse} open adverse media hits`;
  }

  rescreenNow() {
    if (!this.entityIdDetails || !this.entityIdDetails.entityId || this.rescreening) {
      return;
    }
    this.rescreening = true;
    this.entityMonitoringService.rescreen(this.entityIdDetails.entityId).subscribe(
      response => {
        this.rescreening = false;
        const ok = response && response.flagResult;
        const msg = (response && response.message)
          || (ok ? 'Re-screen requested.' : 'Re-screen request failed.');
        this.snackBarService.showSimpleMsj(msg);
        if (ok) {
          this.entityMonitoringService.matchDataChanged.emit();
          this.refreshAll();
        }
      },
      err => {
        this.rescreening = false;
        this.snackBarService.showSimpleMsj((err && err.message) || 'Re-screen request failed.');
      }
    );
  }

  trackOrUntrack() {
    if (!this.entityIdDetails || !this.entityIdDetails.entityId || this.trackingNow) {
      return;
    }
    this.trackingNow = true;
    const entityId = this.entityIdDetails.entityId;
    const observable = this.isMonitored
      ? this.entityMonitoringService.untrack(entityId)
      : this.entityMonitoringService.track(entityId);

    observable.subscribe(
      response => {
        this.trackingNow = false;
        const msg = response && response.message;
        const arrResult = response && response.arrResult;
        if (msg && (!arrResult || Object.keys(arrResult || {}).length === 0)) {
          this.snackBarService.showSimpleMsj(msg);
        } else {
          this.isMonitored = !this.isMonitored;
          this.snackBarService.showSimpleMsj(
            this.isMonitored ? 'Entity registered for permanent monitoring.' : 'Entity monitoring disabled.'
          );
          this.entityMonitoringService.matchDataChanged.emit();
        }
      },
      err => {
        this.trackingNow = false;
        this.snackBarService.showSimpleMsj(
          err.message || (this.isMonitored ? 'Failed to disable monitoring.' : 'Failed to register for monitoring.')
        );
      }
    );
  }

  deleteAllHits() {
    if (!this.entityIdDetails || !this.entityIdDetails.entityId || this.deletingAllHits) { return; }
    const openTotal = (this.openCounts && this.openCounts.total) || 0;
    const histTotal = (this.historyRows && this.historyRows.length) || 0;
    const count = openTotal + histTotal;
    if (count === 0) {
      this.snackBarService.showSimpleMsj('No hits to delete.');
      return;
    }
    if (!confirm('Delete all ' + count + ' monitoring hits (sanctions, PEP, adverse media) for this entity? This cannot be undone.')) {
      return;
    }
    this.deletingAllHits = true;
    this.entityMonitoringService.deleteAllHits(this.entityIdDetails.entityId).subscribe(
      response => {
        this.deletingAllHits = false;
        const ok = response && response.flagResult;
        this.snackBarService.showSimpleMsj(ok ? 'All hits deleted.' : (response && response.message) || 'Delete failed.');
        if (ok) {
          this.entityMonitoringService.matchDataChanged.emit();
          this.refreshAll();
        }
      },
      err => {
        this.deletingAllHits = false;
        this.snackBarService.showSimpleMsj(err.message || 'Failed to delete hits.');
      }
    );
  }

  verifyNow() {
    if (!this.entityIdDetails || !this.entityIdDetails.entityId || this.verifyingNow) {
      return;
    }
    this.verifyingNow = true;
    const config = new MdDialogConfig();
    config.viewContainerRef = this.viewContainerRef;
    const dialogRef = this.dialog.open(VerifyNowDialogComponent, config);
    dialogRef.componentInstance.loading = true;

    this.entityMonitoringService.verifyNow(this.entityIdDetails.entityId).subscribe(
      response => {
        dialogRef.componentInstance.loading = false;
        this.verifyingNow = false;
        const arrResult = response && response.arrResult;
        const msg = response && response.message;
        if (msg && (!Array.isArray(arrResult) || arrResult.length === 0)) {
          dialogRef.componentInstance.errorMessage = msg;
        } else if (Array.isArray(arrResult)) {
          dialogRef.componentInstance.results = arrResult;
        } else {
          dialogRef.componentInstance.results = [];
        }
      },
      err => {
        dialogRef.componentInstance.loading = false;
        this.verifyingNow = false;
        dialogRef.componentInstance.errorMessage = err.message || 'Screening request failed';
      }
    );
  }

  isRowSelectable(row: MonitoringRow): boolean {
    return !!row && row.listType === 'open';
  }

  isRowSelected(row: MonitoringRow): boolean {
    return this.selectedRowKeys.has(this.getRowKey(row));
  }

  toggleRowSelection(row: MonitoringRow, checked: boolean) {
    const key = this.getRowKey(row);
    if (checked) {
      this.selectedRowKeys.add(key);
    } else {
      this.selectedRowKeys.delete(key);
    }
  }

  get selectableOpenRows(): MonitoringRow[] {
    return (this.filteredRows || []).filter(r => this.isRowSelectable(r));
  }

  get selectedOpenRows(): MonitoringRow[] {
    return this.selectableOpenRows.filter(r => this.isRowSelected(r));
  }

  get selectedOpenCount(): number {
    return this.selectedOpenRows.length;
  }

  get allOpenSelected(): boolean {
    const open = this.selectableOpenRows;
    return open.length > 0 && open.every(r => this.isRowSelected(r));
  }

  toggleSelectAllOpen(checked: boolean) {
    this.selectableOpenRows.forEach(r => {
      const key = this.getRowKey(r);
      if (checked) {
        this.selectedRowKeys.add(key);
      } else {
        this.selectedRowKeys.delete(key);
      }
    });
  }

  clearSelection() {
    this.selectedRowKeys.clear();
  }

  resolveSelected() {
    const rows = this.selectedOpenRows;
    if (!rows.length) {
      return;
    }
    const config = new MdDialogConfig();
    config.viewContainerRef = this.viewContainerRef;
    this.dialogRef = this.dialog.open(ResolveBulkHitsComponent, config);
    this.dialogRef.componentInstance.rows = rows.map(r => ({
      type: r.type,
      title: r.title,
      relationEntityId: r.relationEntityId,
      raw: r.raw
    }));

    this.dialogRef.afterClosed().subscribe(result => {
      this.dialogRef = null;
      if (!result || !result.resolvedCount) {
        return;
      }
      this.clearSelection();
      this.entityMonitoringService.matchDataChanged.emit();
      this.refreshAll();
    });
  }

  openHitDetail(row: MonitoringRow) {
    if (!row || !this.entityIdDetails) {
      return;
    }
    const config = new MdDialogConfig();
    config.viewContainerRef = this.viewContainerRef;
    this.dialogRef = this.dialog.open(HitDetailDialogComponent, config);
    this.dialogRef.componentInstance.row = {
      type: row.type,
      listName: row.listName,
      hitTokens: row.hitTokens,
      source: row.source,
      subTitle: row.subTitle,
      hitDate: row.hitDate,
      matchResolution: row.matchResolution,
      falseMatchReason: row.falseMatchReason,
      comments: row.comments,
      relationEntityId: row.relationEntityId,
      listType: row.listType === 'open' ? 'open' : 'history',
      raw: row.raw
    };
    this.dialogRef.componentInstance.entityId = this.entityIdDetails.entityId;

    this.dialogRef.afterClosed().subscribe(result => {
      this.dialogRef = null;
      if (!result || !result.resolved) {
        return;
      }

      const patchedRaw = Object.assign({}, row.raw, {
        matchResolution: result.matchResolution,
        falseMatchReason: result.falseMatchReason,
        comments: result.comments
      });

      const resolvedRow: MonitoringRow = {
        id: `recent:${row.type}:${(new Date()).getTime()}`,
        type: row.type,
        listType: 'history',
        hitDate: patchedRaw.date,
        source: row.source,
        listName: row.listName,
        hitTokens: row.hitTokens,
        title: row.listName,
        subTitle: row.subTitle,
        matchResolution: patchedRaw.matchResolution,
        falseMatchReason: patchedRaw.falseMatchReason,
        comments: patchedRaw.comments,
        relationEntityId: row.relationEntityId,
        raw: patchedRaw
      };

      this.recentResolvedRows = [resolvedRow].concat(this.recentResolvedRows || []);
      this.openRows = (this.openRows || []).filter(r => r.id !== row.id);
      this.updateOpenCounts();
      this.rebuildRows();
      this.entityMonitoringService.matchDataChanged.emit();
      this.refreshAll();
    });
  }
}




// WEBPACK FOOTER //
// ./src/app/entities/monitoring/dashboard/monitoring-dashboard.component.ts